import { ReactElement, Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Option } from '../constants/telosair-interfaces';
import {
  DROPDOWN_CONTROL_CHILD_CLASS,
  DROPDOWN_CONTROL_CLASS
} from '../constants/global-constants';
import TruncatedText from '../components/truncatedText';

type DropdownControlProps = {
  controlPlaceholder: string;
  /** Caption drawn above the button saying what the dropdown selects. */
  label?: string;
  options: Option<any>[];
  onSelect: (option: Option<any>) => void;
  onChildSelect?: (option: Option<any>) => void;
  theme?: 'bg-dark' | 'bg-light';
  disabled?: boolean;
  position?: 'left' | 'right';
};

type ItemProps = {
  options: Option<any>[];
  onSelect: (option: Option<any>) => void;
  onChildSelect?: (option: Option<any>) => void;
  option: Option<any>;
  index: number;
};
/**
 * Utility function for conditionally joining CSS classes.
 * @param classes - CSS classes to conditionally include.
 * @returns - Joined CSS classes.
 */
function classNames(...classes: any): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Component to render each item of Dropdown.
 * @param props - Properties of the Dropdown Item.
 * @returns - React Element.
 */
function EachItem(props: ItemProps): ReactElement {
  const { option, onSelect, options, index, onChildSelect } = props;
  const [expanded, setExpanded] = useState(false);
  /**
   * Function to handle on expand of childOptions.
   * @param e - Click event.
   */
  function onExpand(e: any): void {
    e.preventDefault();
    setExpanded((prev) => !prev);
  }
  return (
    <>
      <Menu.Item key={option.name}>
        {({ active }) => (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events
          <button
            type="button"
            onClick={() => onSelect(option)}
            className={classNames(
              active ? 'dark:bg-background-500 dark:text-white' : 'text-gray-400',
              index !== options.length - 1 && 'border-b',
              'flex w-full justify-between items-center px-4 py-2 text-sm gap-1 cursor-pointer border-b-gray-300 dark:border-b-gray-800'
            )}
          >
            <span className="max-w-[10rem]">
              <TruncatedText text={`${option.name}`} />
            </span>
            {option.childOptions && (
              <button
                type="button"
                className="w-fit px-2 hover:bg-gray-600/50 rounded flex justify-center items-center cursor-pointer"
                onClick={onExpand}
              >
                {!expanded ? (
                  <span className="material-icons-outlined text-xl leading-none text-gray-400">
                    expand_more
                  </span>
                ) : (
                  <span className="material-icons-outlined text-xl leading-none text-gray-400">
                    expand_less
                  </span>
                )}
              </button>
            )}
          </button>
        )}
      </Menu.Item>
      {expanded &&
        option.childOptions &&
        option.childOptions?.map((childOption, childIndex) => (
          <Menu.Item key={childOption.id}>
            {({ active }) => {
              const parentLength = option.childOptions?.length || 0;
              return (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events
                <button
                  type="button"
                  onClick={() => onChildSelect && onChildSelect(childOption)}
                  className={classNames(
                    active ? 'dark:bg-background-500 dark:text-white' : 'text-gray-400',
                    childIndex !== parentLength - 1 && 'border-b',
                    'ml-[10%] px-4 py-2 text-sm cursor-pointer border-b-gray-300 dark:border-b-gray-800 block w-[90%]  text-left'
                  )}
                >
                  {childOption.name}
                </button>
              );
            }}
          </Menu.Item>
        ))}
    </>
  );
}

/**
 * A DropdownControl for renders the list of dropdown.
 * @param props - Properties of the DropdownControlNew.
 * @returns - React Element.
 */
export default function DropdownControl(props: DropdownControlProps): ReactElement {
  const {
    controlPlaceholder,
    label,
    options,
    onSelect,
    onChildSelect,
    theme,
    disabled,
    position = 'left'
  } = props;

  let className = 'bg-container-700 text-gray-200 rounded-md border-0';
  if (theme === 'bg-light') {
    className = 'bg-transparent border border-gray-700 text-gray-400 rounded';
  }
  return (
    <Menu as="div" className="relative inline-block text-left z-20">
      <div className="flex flex-col">
        {label && (
          <span className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            {label}
          </span>
        )}
        <Menu.Button
          disabled={disabled}
          className={`${DROPDOWN_CONTROL_CLASS} ${className} ${disabled && 'opacity-60'}`}
        >
          <span>{controlPlaceholder}</span>
          <span className={DROPDOWN_CONTROL_CHILD_CLASS}>expand_more</span>
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={`absolute ${
            position === 'left' ? 'left-0' : 'right-0'
          } z-10 mt-2 w-56 origin-top-right divide-y divide-gray-700 rounded-md bg-white dark:bg-background-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-[60vh] overflow-y-auto`}
        >
          <div className="py-1">
            {options.map((option, index) => (
              <EachItem
                option={option}
                options={options}
                onSelect={onSelect}
                index={index}
                key={option.name}
                onChildSelect={onChildSelect}
              />
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
